import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {get} from 'lodash'

import {getEntitlementById} from '../../utils/services'
import usePapiEntities from '../shared/usePapiEntities'
import Placeholder from '../shared/Placeholder'
import {itemStyle, infoStyle, idStyle} from '../shared/styles'
import {renderName, idCheck} from '../shared'
import EntitlementIcon from './EntitlementIcon'

const renderAttachedApplication = (showAttachedApplication, application, size, showParens = true) =>
    showAttachedApplication ? (
        <div className="id" data-testid="application" style={idStyle(size)}>
            {showParens
                ? `(Application: ${application.name.toUpperCase()})`
                : `Application: ${application.name.toUpperCase()}`}
        </div>
    ) : null

const renderEntitlement = (entitlement, showLink, showAttachedApplication, size, multiLine) => (
    <div className="papi-item" style={itemStyle}>
        <EntitlementIcon size={size} />
        <div
            className="item-info"
            style={{...infoStyle, flexDirection: multiLine ? 'column' : 'row'}}
        >
            {renderName(
                entitlement,
                `applications/${get(entitlement, 'application.id', '')}`,
                showLink && entitlement.application,
                size
            )}
            {renderAttachedApplication(
                showAttachedApplication && entitlement.application,
                entitlement.application,
                size,
                !multiLine
            )}
            {entitlement.disabled ? (
                <span data-testid="not-selectable"> (not selectable)</span>
            ) : (
                ''
            )}
        </div>
    </div>
)

const FetchEntitlementByIdAndRender = ({
    id,
    showLink,
    showAttachedApplication,
    alwaysLoading,
    size,
    multiLine,
    tokenProvider,
    profileApiUrl
}) => {
    const {entity, setEntity, loading, setLoading} = usePapiEntities(true)

    useEffect(() => {
        const fetchEntitlement = async () => {
            if (alwaysLoading) {
                setLoading(true)
            } else if (id) {
                try {
                    const entitlement = await getEntitlementById(id, tokenProvider, profileApiUrl)
                    setEntity(entitlement)
                    setLoading(false)
                } catch (error) {
                    console.error(error)
                    setLoading(false)
                }
            }
        }
        fetchEntitlement()
    }, [id, alwaysLoading])

    return loading ? (
        <Placeholder />
    ) : (
        renderEntitlement(entity, showLink, showAttachedApplication, size, multiLine)
    )
}

const PapiEntitlement = ({
    id,
    entitlement,
    showLink,
    alwaysLoading,
    size = 'medium',
    showAttachedApplication = false,
    multiLine = false,
    tokenProvider,
    profileApiUrl
}) =>
    entitlement ? (
        renderEntitlement(entitlement, showLink, showAttachedApplication, size, multiLine)
    ) : (
        <FetchEntitlementByIdAndRender
            id={id}
            showLink={showLink}
            showAttachedApplication={showAttachedApplication}
            alwaysLoading={alwaysLoading}
            size={size}
            multiLine={multiLine}
            tokenProvider={tokenProvider}
            profileApiUrl={profileApiUrl}
        />
    )

PapiEntitlement.displayName = 'PapiEntitlement'

PapiEntitlement.propTypes = {
    entitlement: PropTypes.shape({
        name: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        application: PropTypes.shape({
            name: PropTypes.string.isRequired,
            id: PropTypes.string.isRequired
        })
    }),
    id: idCheck('entitlement'),
    showLink: PropTypes.bool,
    showAttachedApplication: PropTypes.bool,
    multiLine: PropTypes.bool,
    tokenProvider: PropTypes.func,
    profileApiUrl: PropTypes.string
}

export default PapiEntitlement
